import * as Sentry from "@sentry/browser";
import { useCallback } from "react";
import {
  ErrorBoundaryPropsWithFallback,
  FallbackProps,
  ErrorBoundary as ReactErrorBoundary,
} from "react-error-boundary";
import { ErrorFallback } from "@/core/components/ErrorFallback";

interface Props
  extends Omit<ErrorBoundaryPropsWithFallback, "onError" | "FallbackComponent" | "fallback"> {
  componentName?: string;
  onError?: (error: Error, errorInfo: { componentStack: string }) => void;
  showRetry?: boolean;
  showReload?: boolean;
  centered?: boolean;
}

export function ErrorBoundary({
  children,
  componentName,
  onError,
  showRetry = true,
  showReload = false,
  centered = false,
  ...props
}: Props) {
  const handleError = (error: Error, errorInfo: { componentStack: string }) => {
    Sentry.withScope((scope) => {
      scope.setTag("errorBoundary", componentName || "Unknown");
      scope.setContext("errorInfo", errorInfo);
      Sentry.captureException(error);
    });

    if (onError) {
      onError(error, errorInfo);
    }
  };

  const fallbackRender = useCallback(
    (fallbackProps: FallbackProps) => (
      <ErrorFallback
        {...fallbackProps}
        componentName={componentName}
        showRetry={showRetry}
        showReload={showReload}
        centered={centered}
      />
    ),
    [componentName, showRetry, showReload, centered],
  );

  return (
    <ReactErrorBoundary fallbackRender={fallbackRender} onError={handleError} {...props}>
      {children}
    </ReactErrorBoundary>
  );
}
